/**
 * Distance helpers for the "use my location" branch sorting.
 * Zero dependency: haversine + short label, computed in the browser only.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;

export function haversineKm(a: LatLng, b: LatLng): number {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** "à 2,4 km" / "2.4 km away" — metres below 1 km. */
export function formatDistance(km: number, lang: "fr" | "en"): string {
  if (!Number.isFinite(km)) return "";
  if (km < 1) {
    const m = Math.max(50, Math.round(km * 1000 / 50) * 50);
    return lang === "fr" ? `à ${m} m` : `${m} m away`;
  }
  const rounded = km < 10 ? km.toFixed(1) : String(Math.round(km));
  const value = lang === "fr" ? rounded.replace(".", ",") : rounded;
  return lang === "fr" ? `à ${value} km` : `${value} km away`;
}

export function hasCoords(b: {
  gps_lat?: number | null;
  gps_lng?: number | null;
}): boolean {
  return (
    typeof b.gps_lat === "number" &&
    typeof b.gps_lng === "number" &&
    Number.isFinite(b.gps_lat) &&
    Number.isFinite(b.gps_lng) &&
    !(b.gps_lat === 0 && b.gps_lng === 0)
  );
}
